import axios from 'axios';
import ExpiresAfter, {type ExpiresAfterName, expiresAfterName} from '../domain/ExpiresAfter';
import {requestUploadPath, type RequestUploader} from "remindersUpload/requestUploader";
import type {AssertNoSpecDrift, RequestSpecDrift, Schemas, SpecDrift} from 'api/schemaAssert';
import {
	ACCOUNT_FILES_TRANSFERS_PATH,
	INSTANT_TRANSFER_UPLOADS_PATH,
	multipartCompletionsPath,
	multipartInitiationsPath,
	multipartPartUrlsPath,
	multipartUploadedPartsPath,
	TRANSFER_UPLOADS_PATH,
} from 'api/endpointPaths.mjs';
import type {PersonalizationDraftRequest} from 'home/personalization/personalizationDraft';
import type {SenderCompany} from 'sender/senderChoice';

/**
 * HTTP client for the presigned direct-to-S3 upload endpoints — the ONLY place that knows these routes
 * and payload shapes. Pure data in/out (no UI, no file slicing); the orchestration lives in
 * {@link ../core/upload/EncryptedUploadClient}. Axios already has `baseURL` (REACT_APP_URL) + the Firebase auth
 * interceptor configured globally (see AppProviders / AxiosUtils), so paths here are relative.
 *
 * Mirrors the backend DTOs: UploadInitRequest/Response, UploadCompleteRequest/Response, UploadEmailRequest.
 */

/** Declared metadata for one file to upload (no bytes). */
export interface FileMeta {
	name: string;
	size: number;
	contentType: string;
	/**
	 * Optional folder-relative path INCLUDING the leaf name (`docs/report.pdf`, ui#94/api#46) —
	 * sanitized before sending, omitted entirely for flat files.
	 */
	relativePath?: string;
}

export interface UploadInitRequest {
	message?: string;
	keepOnline: boolean;
	expireAfter: ExpiresAfter;
	peerId?: string;
	/** UUIDv7 idempotency key; required whenever either recipient source is present (api#136). */
	initializationId?: string;
	files: FileMeta[];
	/** Direct recipient source reserved before upload; omitted for link/group-only initialization. */
	toEmails?: string[];
	/** Saved-group source reserved before upload; omitted for link/direct-only initialization. */
	groupIds?: string[];
	/** Optional download password (API contract v3) — stored hashed server-side, never returned. */
	password?: string;
	/** Optional download cap (API contract v3) — int >= 1; absent = unlimited. */
	maxDownloads?: number;
	/** Per-transfer download-alerts opt-in (api#92); the API authorizes arming via `downloadNotifications`. */
	notifyOnDownload?: boolean;
	/**
	 * Per-transfer expiry-recovery opt-in (api#443) — arming requires the Premium
	 * `recoverExpiredTransfers` capability, and makes the api retain the bytes past the link's expiry
	 * so the transfer can be recovered once. Omitted/false stores nothing extra.
	 */
	recoveryOptIn?: boolean;
	/**
	 * {@code true} for an E2EE chunked transfer (api#28, epic #55): the files are client-side encrypted
	 * and uploaded through the batch multipart endpoints below. The init answers each file's id, and the batch
	 * initiate then mints each file's upload id and part size; part URLs are signed only after that.
	 */
	encrypted?: boolean;
	/**
	 * {@code true} for the server-escrow flow (epic #61 Option A, api#29): the AES file key is wrapped
	 * with the server's KMS public key and sent per file on the multipart initiate (see
	 * {@link MultipartBatchFileInit.wrappedKey}); the server persists it so it can KMS-`Decrypt` on
	 * download. {@code false} (default) is Zero-Knowledge — the raw key lives only in the URL fragment
	 * and nothing key-related is ever sent. Only meaningful alongside {@code encrypted: true}.
	 */
	keyEscrow?: boolean;
	/**
	 * Per-transfer personalization (api#461): the visual snapshot this ONE transfer renders, overriding
	 * the account-level Personalization sheet and expiring with the transfer. Absent = unpersonalized.
	 *
	 * Every plan includes the look (#525); the create flow sends it only once the sender has enabled the sheet.
	 */
	personalization?: PersonalizationDraftRequest;
	/**
	 * The company this transfer is sent as (kitecorp/transferify-ui#3186), or absent for one a person sends. Free on
	 * every plan. Beside the look rather than inside it: the look may be discarded or attached later, who sends the
	 * files may not.
	 */
	senderCompany?: SenderCompany;
	/**
	 * The settled over-limit purchase this transfer spends (api#823, ui#1454).
	 *
	 * NAMED, never auto-applied: the api spends only the allowance the init points at, so a 1 TB
	 * purchase can never be quietly consumed by a 6 GB send. An id that does not cover the declared
	 * size is refused with `error.transfer.overLimit.notConsumable`, and an over-cap init carrying none
	 * is refused with `error.transfer.overLimit.paymentRequired`. Absent = an ordinary within-cap send.
	 */
	overLimitAllowanceId?: string;
}

/**
 * One declared file as the init answers it: its id, which every later upload call names. Each file's storage
 * upload is minted afterwards by {@link transferApi.initiateMultipartBatch}, which answers its upload id and
 * part size.
 */
export interface FileUpload {
	fileId: string;
}

/** The answer to an upload init, and to an addition of files to a sent transfer (`POST /transfer/{id}/files`). */
export interface UploadInitResponse {
	/**
	 * The ENCODED transfer id — the form the `/transfer/uploads/{transferId}/…` routes take. An addition is
	 * addressed by the RAW uuid, so it must thread this through to the upload engine rather than re-derive either.
	 */
	transferId: string;
	files: FileUpload[];
}

/**
 * Body of `POST /account/files/transfers` (`StoredFilesTransferRequest`, kitecorp/transferify-api-service#2597): the
 * hosted init's settings, with the stored files' ids in place of files to upload. Nothing is uploaded and no byte is
 * duplicated: the new transfer's files serve the same stored bytes, which the api keeps for as long as the latest
 * link serving them needs them. The api requires `encrypted` and `keyEscrow` (the files keep their escrowed keys),
 * 1 to 10,000 `sourceFileIds`, and an `initializationId` whenever `toEmails` or `groupIds` is sent.
 */
export interface StoredLinkRequest extends Omit<UploadInitRequest, 'files'> {
	sourceFileIds: string[];
}

/** One file of the new transfer (`StoredFilesTransferFile`): the new file, and the stored file it serves the bytes of. */
export interface StoredLinkFile {
	fileId: string;
	sourceFileId: string;
}

/**
 * The answer (`StoredFilesTransferResponse`): the transfer, already complete. `transferId` is the ENCODED id the
 * `/transfer/uploads/{transferId}/email` and `/group` deliveries take, `files` are listed in the request's order,
 * and `wasFirst` says whether this is the account's first ever sent transfer.
 */
export interface StoredLinkResponse {
	transferId: string;
	files: StoredLinkFile[];
	url: string;
	wasFirst: boolean;
}

/** A part the browser finished uploading, reported on complete (S3 validates the ETag). */
export interface CompletedPart {
	partNumber: number;
	eTag: string;
}

export interface UploadCompleteRequest {
	/**
	 * The personalization to attach as the upload completes (api#1099), or absent to leave the transfer
	 * exactly as the initialization left it.
	 *
	 * The preview-first builder (ui#1814) composes WHILE the bytes upload, so the finished draft does
	 * not exist when the init runs. The api validates it through the same funding, arity, ownership and
	 * credit-spend code the init uses, and it is write-once: a second, DIFFERENT personalization is
	 * refused, while a completion retried with the same one changes nothing.
	 */
	personalization?: PersonalizationDraftRequest;
}

export interface UploadCompleteResponse {
	url: string;
	/**
	 * The account's FIRST ever transfer, decided by the server's atomic marker (api#647) — true for
	 * exactly one completion per account, ever, so the client can fire its one-shot conversion without
	 * keeping any state of its own.
	 */
	wasFirst: boolean;
}

/*
 * The BATCH form of the E2EE multipart calls (kitecorp/transferify-api-service#2193): initiate, sign part
 * urls for, and complete many files of one transfer per call, under `/transfer/uploads/{transferId}/…`.
 * Their bounds live in `api/multipartBatchLimits.ts`. Same key-blindness as the per-file calls: the only
 * key-bearing fields are the escrow wrap and its KMS key id, and never on a Zero-Knowledge transfer.
 */

/** One file's initiate: its id and how it was chunked, plus the escrow wrap on a key-escrow transfer. */
export interface MultipartBatchFileInit {
	fileId: string;
	/** Uniform plaintext bytes per cipher chunk — also the effective part size for all but the last part. */
	cipherChunkSize: number;
	/** How many ciphertext chunks (= parts) the file is split into (1..10000). */
	chunkCount: number;
	/** Total ciphertext size in bytes (header + plaintext + per-chunk GCM tags). */
	ciphertextSize: number;
	/**
	 * Server-escrow only (epic #61 Option A): the AES file key wrapped with the server's KMS public key, as
	 * base64url — EXACTLY what {@link wrapKeyWithServerKey} returns. Required on every transfer that is not
	 * Zero-Knowledge, including one created unencrypted; absent on a Zero-Knowledge one. Never the raw key.
	 */
	wrappedKey?: string;
	/** Server-escrow only: the KMS key id the {@link wrappedKey} was produced for. */
	kmsKeyId?: string;
}

export interface MultipartBatchInitRequest {
	files: MultipartBatchFileInit[];
}

/** One file's storage upload, named on every later call for that file. */
export interface MultipartBatchFileInitiated {
	fileId: string;
	uploadId: string;
	partSize: number;
	chunkCount: number;
}

/** Every requested file's upload, in request order. All or nothing; a repeated initiate answers the same. */
export interface MultipartBatchInitResponse {
	files: MultipartBatchFileInitiated[];
	partUrlTtlSeconds: number;
}

/** One file's parts to sign. */
export interface MultipartBatchFileParts {
	fileId: string;
	uploadId: string;
	partNumbers: number[];
}

export interface MultipartBatchPartUrlsRequest {
	files: MultipartBatchFileParts[];
}

export interface MultipartBatchPartUrl {
	partNumber: number;
	url: string;
}

export interface MultipartBatchFilePartUrls {
	fileId: string;
	parts: MultipartBatchPartUrl[];
}

/** The signed urls, per file and in request order; PUT each part before {@code expiresInSeconds} elapse. */
export interface MultipartBatchPartUrlsResponse {
	files: MultipartBatchFilePartUrls[];
	expiresInSeconds: number;
}

/** One file's completion: its upload and every part it uploaded. */
export interface MultipartBatchFileComplete {
	fileId: string;
	uploadId: string;
	parts: CompletedPart[];
}

export interface MultipartBatchCompleteRequest {
	files: MultipartBatchFileComplete[];
}

/**
 * One file's outcome. {@code completed: false} means storage could not complete it: the api aborted and
 * forgot its upload, and the file has to be initiated and uploaded again.
 */
export interface MultipartBatchFileCompleted {
	fileId: string;
	completed: boolean;
}

export interface MultipartBatchCompleteResponse {
	/** One outcome per requested file, in request order. */
	files: MultipartBatchFileCompleted[];
	/** {@code true} once every file of the transfer is uploaded — {@code url} is then present. */
	transferComplete: boolean;
	url?: string;
	/** See {@link UploadCompleteResponse.wasFirst}: true only for the call that finished the account's first transfer. */
	wasFirst: boolean;
}

/** The files a browser continuing an interrupted upload asks about (kitecorp/transferify-ui#3204). */
export interface MultipartBatchUploadedPartsRequest {
	fileIds: string[];
}

/** One part storage already holds of an open upload. */
export interface MultipartUploadedPart {
	partNumber: number;
	eTag: string;
	/** Its stored length, compared with the length this browser would send before it is trusted. */
	size: number;
}

/** One file's state: uploaded, or open with the parts storage holds, or neither and to be initiated. */
export interface MultipartBatchFileUploadedParts {
	fileId: string;
	uploaded: boolean;
	/** Its open storage upload; absent (or null on the wire) when it has none and must be initiated again. */
	uploadId?: string;
	parts: MultipartUploadedPart[];
}

/** Every requested file in request order, and the transfer's readiness. */
export interface MultipartBatchUploadedPartsResponse {
	files: MultipartBatchFileUploadedParts[];
	transferComplete: boolean;
	/** The download-page url once every file of the transfer is uploaded. */
	url?: string;
	/**
	 * This listing recorded a completion storage had already made (a reload during the last completion), and it
	 * finished the account's first transfer: reported as a completion's own answer is.
	 */
	wasFirst: boolean;
}

export interface UploadEmailRequest {
	toEmails: string[];
	message?: string;
}

export interface UploadGroupRequest {
	/** Server-authoritative targets: the API expands current persisted membership. */
	groups: Array<{id: string}>;
	message?: string;
}

/** Sender-facing shape of ONE recipient-delivery call (api-service#233). */
export type RecipientDeliveryResult =
	/** Every recipient of this call was mailed. */
	| 'DELIVERED'
	/** Some were mailed and some were not (opted out, or the provider refused them). */
	| 'PARTIAL'
	/** Nobody was mailed: every recipient had opted out of recipient-facing mail. */
	| 'SUPPRESSED'
	/** This call sent nothing — an earlier attempt already completed the channel. */
	| 'ALREADY_COMPLETED';

/**
 * Per-recipient outcome of one delivery call, returned by BOTH chokepoints (api-service#233).
 *
 * They returned `void` until then, so a send that reached only some recipients was indistinguishable
 * from one that reached all of them. Bounded on purpose: the only addresses that cross this boundary
 * are ones the SENDER supplied, and provider-refused addresses are deliberately never echoed — a
 * failure is a transport fact the sender can only act on by retrying the whole channel.
 * {@link ../core/transfer/deliveryOutcome} turns these into what the sender is actually told.
 */
export interface RecipientDeliveryResponse {
	result: RecipientDeliveryResult;
	/** Mailboxes the provider accepted on this call. */
	deliveredCount: number;
	/** Mailboxes skipped because they opted out of recipient-facing mail. */
	suppressedCount: number;
	/** Mailboxes the provider refused on this call. */
	failedCount: number;
	/** The opted-out addresses, echoed from the recipient list the sender supplied. */
	suppressedRecipients: string[];
}

/** Metadata for one instant (P2P) file — no bytes; {@code magnetURI} is how a peer matches the file. */
export interface InstantFileMeta {
	name: string;
	size: number;
	/** The browser-reported type, read exactly as the hosted {@link FileMeta} reads it (api#1631). */
	contentType: string;
	magnetURI: string;
	/**
	 * Optional folder-relative path INCLUDING the leaf name (`docs/report.pdf`, ui#94/api#46) —
	 * sanitized before sending, omitted entirely for flat files.
	 */
	relativePath?: string;
}

export interface InstantTransferRequest {
	message?: string;
	expireAfter: ExpiresAfter;
	peerId?: string;
	files: InstantFileMeta[];
	/** Optional download password (API contract v3) — stored hashed server-side, never returned. */
	password?: string;
	/** Optional download cap (API contract v3) — int >= 1; absent = unlimited. */
	maxDownloads?: number;
	/** Per-transfer download-alerts opt-in (api#92); the API authorizes arming via `downloadNotifications`. */
	notifyOnDownload?: boolean;
	/** Per-transfer personalization (api#461) — see {@link UploadInitRequest.personalization}. */
	personalization?: PersonalizationDraftRequest;
	/** The company this transfer is sent as (kitecorp/transferify-ui#3186) — see {@link UploadInitRequest.senderCompany}. */
	senderCompany?: SenderCompany;
	/**
	 * The settled over-limit purchase this peer-to-peer send is admitted under (api#1039, ui#1719).
	 *
	 * NAMED, never auto-applied, exactly as on the hosted init: the registration spends the purchase the
	 * composer showed the sender, so a 1 TB allowance can never be quietly spent on a 6 GB instant send.
	 * Absent is the ordinary case.
	 */
	overLimitAllowanceId?: string;
}

export interface InstantTransferResponse {
	url: string;
	/**
	 * The SERVER-issued sender peer id (api#72), minted only when the request omitted `peerId`; null
	 * when the client supplied its own (legacy protocol), and absent on an api predating the field.
	 */
	peerId?: string | null;
	/**
	 * Signed proof of ownership of {@link peerId}, presented in the signaling CONNECT `peerToken`
	 * header. Null/absent exactly when `peerId` is — a client-chosen id is never tokenized.
	 */
	peerToken?: string | null;
	/** See {@link UploadCompleteResponse.wasFirst}. */
	wasFirst: boolean;
}

/** Live download counter for a transfer (API contract v3 `GET /transfers/{id}/stats`). */
export interface TransferStatsResponse {
	downloads: number;
}

/*
 * OpenAPI conformance (#208): every DTO above is bound to its generated spec type — `tsc -b` fails,
 * naming the field, when the api-service contract drifts. See src/api/schemaAssert.ts for why the
 * documented hand-written DTOs stay (springdoc marks everything optional) and what Ignore means.
 */
/**
 * The create bodies AS SERIALIZED: `expireAfter` travels as the api's `ExpireAfter` NAME
 * (kitecorp/transferify-api-service#690), the same form the PATCH body and every response projection
 * already use. The app keeps working in the numeric {@link ExpiresAfter} either side of this boundary —
 * the enum's numeric values are now an app-internal identity that no longer has to match anything
 * server-side, which is exactly why the ordinal pin in `ExpiresAfter.test.ts` became a name pin.
 *
 * The spec bindings below deliberately target these wire types rather than the app-facing ones: a
 * numeric enum NARROWS the spec's old `number`, so `RequestSpecDrift` accepted it and could never have
 * caught a renumbering. Bound to the string union, it now fails compilation the moment the two ladders
 * disagree on a NAME.
 */
type WireUploadInitRequest = Omit<UploadInitRequest, 'expireAfter'> & {expireAfter: ExpiresAfterName};
type WireInstantTransferRequest = Omit<InstantTransferRequest, 'expireAfter'> & {expireAfter: ExpiresAfterName};
type WireStoredLinkRequest = Omit<StoredLinkRequest, 'expireAfter'> & {expireAfter: ExpiresAfterName};

/** Maps a create body onto the wire: unchanged but for the retention, which travels by NAME. */
function toWireCreate<T extends {expireAfter: ExpiresAfter}>(
	request: T,
): Omit<T, 'expireAfter'> & {expireAfter: ExpiresAfterName} {
	return {...request, expireAfter: expiresAfterName(request.expireAfter)};
}

export type _FileMeta = AssertNoSpecDrift<RequestSpecDrift<FileMeta, Schemas['FileMeta']>>;
export type _UploadInitRequest = AssertNoSpecDrift<RequestSpecDrift<WireUploadInitRequest, Schemas['UploadInitRequest']>>;
export type _StoredLinkRequest =
	AssertNoSpecDrift<RequestSpecDrift<WireStoredLinkRequest, Schemas['StoredFilesTransferRequest']>>;
export type _StoredLinkFile =
	AssertNoSpecDrift<SpecDrift<StoredLinkFile, Schemas['StoredFilesTransferResponse']['files'][number]>>;
export type _StoredLinkResponse =
	AssertNoSpecDrift<SpecDrift<StoredLinkResponse, Schemas['StoredFilesTransferResponse'], 'files'>>;
export type _SenderCompany = AssertNoSpecDrift<RequestSpecDrift<SenderCompany, Schemas['SenderCompany']>>;
// Each entry is bound field by field as well as through the whole response: the response's binding compares
// the `files` list by assignability, which would not notice the api dropping an entry field this client reads.
export type _FileUpload =
	AssertNoSpecDrift<SpecDrift<FileUpload, Schemas['UploadInitResponse']['files'][number]>>;
export type _UploadInitResponse =
	AssertNoSpecDrift<SpecDrift<UploadInitResponse, Schemas['UploadInitResponse']>>;
export type _CompletedPart = AssertNoSpecDrift<RequestSpecDrift<CompletedPart, Schemas['PartComplete']>>;
export type _UploadCompleteRequest = AssertNoSpecDrift<RequestSpecDrift<UploadCompleteRequest, Schemas['UploadCompleteRequest']>>;
export type _UploadCompleteResponse = AssertNoSpecDrift<SpecDrift<UploadCompleteResponse, Schemas['UploadCompleteResponse']>>;
// CompletedPart is shared by the batch and per-file multipart completions; the spec models them as
// the (identical) PartComplete and MultipartCompletePart schemas, so bind it to both.
export type _CompletedPartMultipart = AssertNoSpecDrift<RequestSpecDrift<CompletedPart, Schemas['MultipartCompletePart']>>;
export type _MultipartBatchFileInit = AssertNoSpecDrift<RequestSpecDrift<MultipartBatchFileInit, Schemas['MultipartBatchFileInit']>>;
export type _MultipartBatchInitRequest =
	AssertNoSpecDrift<RequestSpecDrift<MultipartBatchInitRequest, Schemas['MultipartBatchInitRequest']>>;
export type _MultipartBatchFileInitiated =
	AssertNoSpecDrift<SpecDrift<MultipartBatchFileInitiated, Schemas['MultipartBatchFileInitiated']>>;
export type _MultipartBatchInitResponse =
	AssertNoSpecDrift<SpecDrift<MultipartBatchInitResponse, Schemas['MultipartBatchInitResponse']>>;
export type _MultipartBatchFileParts = AssertNoSpecDrift<RequestSpecDrift<MultipartBatchFileParts, Schemas['MultipartBatchFileParts']>>;
export type _MultipartBatchPartUrlsRequest =
	AssertNoSpecDrift<RequestSpecDrift<MultipartBatchPartUrlsRequest, Schemas['MultipartBatchPartUrlsRequest']>>;
export type _MultipartBatchPartUrl = AssertNoSpecDrift<SpecDrift<MultipartBatchPartUrl, Schemas['MultipartBatchPartUrl']>>;
export type _MultipartBatchFilePartUrls =
	AssertNoSpecDrift<SpecDrift<MultipartBatchFilePartUrls, Schemas['MultipartBatchFilePartUrls']>>;
export type _MultipartBatchPartUrlsResponse =
	AssertNoSpecDrift<SpecDrift<MultipartBatchPartUrlsResponse, Schemas['MultipartBatchPartUrlsResponse']>>;
export type _MultipartBatchFileComplete =
	AssertNoSpecDrift<RequestSpecDrift<MultipartBatchFileComplete, Schemas['MultipartBatchFileComplete']>>;
export type _MultipartBatchCompleteRequest =
	AssertNoSpecDrift<RequestSpecDrift<MultipartBatchCompleteRequest, Schemas['MultipartBatchCompleteRequest']>>;
export type _MultipartBatchFileCompleted =
	AssertNoSpecDrift<SpecDrift<MultipartBatchFileCompleted, Schemas['MultipartBatchFileCompleted']>>;
export type _MultipartBatchCompleteResponse =
	AssertNoSpecDrift<SpecDrift<MultipartBatchCompleteResponse, Schemas['MultipartBatchCompleteResponse']>>;
export type _MultipartBatchUploadedPartsRequest =
	AssertNoSpecDrift<RequestSpecDrift<MultipartBatchUploadedPartsRequest, Schemas['MultipartBatchUploadedPartsRequest']>>;
export type _MultipartUploadedPart = AssertNoSpecDrift<SpecDrift<MultipartUploadedPart, Schemas['MultipartUploadedPart']>>;
export type _MultipartBatchFileUploadedParts =
	AssertNoSpecDrift<SpecDrift<MultipartBatchFileUploadedParts, Schemas['MultipartBatchFileUploadedParts']>>;
export type _MultipartBatchUploadedPartsResponse =
	AssertNoSpecDrift<SpecDrift<MultipartBatchUploadedPartsResponse, Schemas['MultipartBatchUploadedPartsResponse']>>;
export type _UploadEmailRequest = AssertNoSpecDrift<RequestSpecDrift<UploadEmailRequest, Schemas['UploadEmailRequest']>>;
export type _UploadGroupRequest = AssertNoSpecDrift<RequestSpecDrift<UploadGroupRequest, Schemas['UploadGroupRequest']>>;
export type _UploadGroupRef = AssertNoSpecDrift<RequestSpecDrift<UploadGroupRequest['groups'][number], Schemas['UploadGroupRef']>>;
export type _RecipientDeliveryResponse =
	AssertNoSpecDrift<SpecDrift<RecipientDeliveryResponse, Schemas['RecipientDeliveryResponse']>>;
export type _InstantFileMeta = AssertNoSpecDrift<RequestSpecDrift<InstantFileMeta, Schemas['InstantFileMeta']>>;
export type _InstantTransferRequest = AssertNoSpecDrift<RequestSpecDrift<WireInstantTransferRequest, Schemas['InstantTransferRequest']>>;
// Bound on its own as well as through the two requests that carry it: the nested check inside those
// two only compares the field's TYPE, so a server-required key added to the draft itself (api#461's
// `template` is one today) would go unnoticed until a 400 arrived at a sender's send button.
export type _PersonalizationDraft =
	AssertNoSpecDrift<RequestSpecDrift<PersonalizationDraftRequest, Schemas['PersonalizationDraft']>>;
export type _InstantTransferResponse = AssertNoSpecDrift<SpecDrift<InstantTransferResponse, Schemas['InstantTransferResponse']>>;
export type _TransferStatsResponse = AssertNoSpecDrift<SpecDrift<TransferStatsResponse, Schemas['TransferStatsResponse']>>;

/**
 * How long the composer's Cancel waits for the transfer to be voided before giving up on the refund.
 *
 * The upload clients AWAIT that call before rejecting, and the composer only leaves the progress screen on
 * that rejection — so this is the sender's wait, not a background one. Short deliberately: they are already
 * leaving, and a longer bound buys a refund at the cost of the screen they pressed Cancel to get back to.
 */
export const CANCEL_UPLOAD_TIMEOUT_MS = 5_000;

export const transferApi = {
	/** Initiate: declare the files + settings and get the transfer id and each file's id. */
	async initUpload(request: UploadInitRequest): Promise<UploadInitResponse> {
		const {data} = await axios.post<UploadInitResponse>(TRANSFER_UPLOADS_PATH, toWireCreate(request));
		return data;
	},

	/** Complete: report each part's ETag once uploaded; returns the shareable download-page URL. */
	async completeUpload(transferId: string, request: UploadCompleteRequest): Promise<UploadCompleteResponse> {
		const {data} = await axios.post<UploadCompleteResponse>(`${TRANSFER_UPLOADS_PATH}/${transferId}/complete`, request);
		return data;
	},

	/**
	 * E2EE multipart, batched (kitecorp/transferify-api-service#2193): initiate the storage uploads of many
	 * encrypted files of one transfer in one call. All or nothing, and a repeated call answers the uploads the
	 * first one created, so a retry after a lost response is safe.
	 *
	 * Takes NO `AbortSignal`, for the reason the per-file initiate never did (ui#462): cancelling it
	 * client-side cannot stop the server minting the uploads — the request has already been sent — it only
	 * throws away the upload ids, the one handle that can ever abort them. The caller lets it resolve and then
	 * releases what it minted.
	 */
	async initiateMultipartBatch(transferId: string, request: MultipartBatchInitRequest): Promise<MultipartBatchInitResponse> {
		const {data} = await axios.post<MultipartBatchInitResponse>(multipartInitiationsPath(transferId), request);
		return data;
	},

	/**
	 * E2EE multipart, batched: presign the PUT urls of many parts of many files in one call. A POST because the
	 * list is too long for a query string; it changes nothing.
	 *
	 * `signal` cancels a signing still on the wire: the window signs ahead of the parts it serves, so a
	 * cancelled upload would otherwise leave its presigns running to completion (ui#443).
	 */
	async signMultipartPartsBatch(transferId: string, request: MultipartBatchPartUrlsRequest,
								  signal?: AbortSignal): Promise<MultipartBatchPartUrlsResponse> {
		const {data} = await axios.post<MultipartBatchPartUrlsResponse>(multipartPartUrlsPath(transferId), request, {signal});
		return data;
	},

	/**
	 * E2EE multipart, batched: complete the uploads of many files in one call; the download url arrives with
	 * the call that finished the whole transfer.
	 *
	 * `signal` cancels a completion still on the wire, because this call FINALIZES objects: a cancel landing
	 * while it is outstanding would otherwise publish files the sender just cancelled (ui#462).
	 */
	async completeMultipartBatch(transferId: string, request: MultipartBatchCompleteRequest,
								 signal?: AbortSignal): Promise<MultipartBatchCompleteResponse> {
		const {data} = await axios.post<MultipartBatchCompleteResponse>(multipartCompletionsPath(transferId), request, {signal});
		return data;
	},

	/**
	 * What storage already holds of many files' uploads (kitecorp/transferify-ui#3204), for a browser
	 * continuing an upload a reload, a closed tab or a lost connection interrupted. A file whose upload storage
	 * no longer holds is answered with no upload, so it is initiated again, and one a completion consumed before
	 * its record committed is answered uploaded. Once the transfer is gone (deleted, its link expired, or its
	 * account deleted) it answers 404 with `RESUME_TRANSFER_GONE_ERROR_ID`: nothing is left to continue.
	 *
	 * `signal` cancels a listing still on the wire, like the signing: it changes nothing the sender keeps.
	 */
	async listUploadedParts(transferId: string, request: MultipartBatchUploadedPartsRequest,
							signal?: AbortSignal): Promise<MultipartBatchUploadedPartsResponse> {
		const {data} = await axios.post<MultipartBatchUploadedPartsResponse>(multipartUploadedPartsPath(transferId),
			request, {signal});
		return data;
	},

	/**
	 * Abort one file's in-flight S3 multipart upload (cancel/failure), cleaning up its uploaded parts.
	 *
	 * Cleans up STORAGE and nothing else: the transfer row survives it, and so does anything the creation
	 * already spent on that row. {@link cancelUpload} is what takes the transfer itself down.
	 */
	async abortMultipart(transferId: string, fileId: string, uploadId: string): Promise<void> {
		await axios.delete(`${TRANSFER_UPLOADS_PATH}/${transferId}/files/${fileId}/multipart/${uploadId}`);
	},

	/**
	 * Voids a transfer the sender ABANDONED — the composer's Cancel
	 * (api kitecorp/transferify-api-service#1080, ui kitecorp/transferify-ui#1788).
	 *
	 * <b>Cancelling used to cost a paying sender their purchase.</b> The upload init spends an over-limit
	 * allowance before a single byte moves, and the per-file abort above cleaned up S3 parts while leaving
	 * the transfer — and its spent purchase — behind. Nobody could ever hand that purchase back, because
	 * nothing told the api the send was abandoned at all: there was no transfer-level abort endpoint, which
	 * both upload clients' javadoc recorded as an accepted limitation for as long as it stood. This call is
	 * that endpoint. The api deletes the transfer and returns the purchase when nothing was ever delivered
	 * on it.
	 *
	 * Takes the ENCODED id, like every other route on this path — it is the only transfer id a browser is
	 * ever given, which is exactly why `DELETE /transfer/{uuid}` could not be used from here.
	 */
	async cancelUpload(transferId: string): Promise<void> {
		await axios.delete(`${TRANSFER_UPLOADS_PATH}/${transferId}`, {timeout: CANCEL_UPLOAD_TIMEOUT_MS});
	},

	/**
	 * Live download counter for the sender success screen (API contract v3): no auth beyond knowing
	 * the id — the encoded id from the share URL (`/download/{encodedId}`) is exactly what it takes.
	 */
	async transferStats(transferId: string): Promise<TransferStatsResponse> {
		const {data} = await axios.get<TransferStatsResponse>(`/transfers/${transferId}/stats`);
		return data;
	},

	/** Email the download link for an already-completed presigned transfer to the recipients. */
	async emailTransfer(transferId: string, request: UploadEmailRequest): Promise<RecipientDeliveryResponse> {
		const {data} = await axios.post<RecipientDeliveryResponse>(
			`${TRANSFER_UPLOADS_PATH}/${transferId}/email`, request);
		return data;
	},

	/** Email the download link for an already-completed presigned transfer to the caller's groups. */
	async groupTransfer(transferId: string, request: UploadGroupRequest): Promise<RecipientDeliveryResponse> {
		const {data} = await axios.post<RecipientDeliveryResponse>(
			`${TRANSFER_UPLOADS_PATH}/${transferId}/group`, request);
		return data;
	},

	/**
	 * Makes a new link to stored files (kitecorp/transferify-api-service#2597): one call, nothing uploaded. It
	 * answers 400 with the upload init's messages (plan limits included), 404 "Transfer not found" for any named
	 * file it cannot link whatever the reason, and 409 when a different request reuses the `initializationId`; the
	 * same request sent again answers the link its first attempt made.
	 */
	async createStoredLink(request: StoredLinkRequest): Promise<StoredLinkResponse> {
		const {data} = await axios.post<StoredLinkResponse>(ACCOUNT_FILES_TRANSFERS_PATH, toWireCreate(request));
		return data;
	},

	/** Register an instant (P2P) transfer: metadata + peer id only, no bytes. Returns the shareable URL. */
	async instantTransfer(request: InstantTransferRequest): Promise<InstantTransferResponse> {
		const {data} = await axios.post<InstantTransferResponse>(INSTANT_TRANSFER_UPLOADS_PATH, toWireCreate(request));
		return data;
	},

	/**
	 * Initiate a presigned reminder upload (PRO bucket, PLUS tier server-side); same shapes as initUpload.
	 * The route names who is answering: the contact an emailed request was sent to, or a shared link.
	 */
	async reminderInitUpload(reminderId: string, uploader: RequestUploader, request: UploadInitRequest): Promise<UploadInitResponse> {
		const {data} = await axios.post<UploadInitResponse>(requestUploadPath(reminderId, uploader), toWireCreate(request));
		return data;
	},

	/** Complete a presigned reminder upload; the reminder owner is notified server-side (no URL returned). */
	async reminderCompleteUpload(reminderId: string, uploader: RequestUploader, transferId: string, request: UploadCompleteRequest): Promise<void> {
		await axios.post(`${requestUploadPath(reminderId, uploader)}/${transferId}/complete`, request);
	},
};
